import { ContentContainer } from "@twinkltech/curriculum-architect-component-library";
import { Text } from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";

import { GenericError } from "@/components/GenericError/GenericError";
import {
    ResourceList,
    ResourceListItem,
    ResourceListItemLink,
} from "@/components/ResourceLinkList/ResourceLinkList";
import { UnitHeader } from "@/components/UnitHeader/UnitHeader";

import { LoadingPage } from "@/pages/LoadingPage/LoadingPage";

import { UnitInfo } from "@/utils/getUnitFromCell/getUnitFromCell";
import { ResourceHelper } from "@/utils/ResourceHelper/ResourceHelper";

import { paths } from "@/paths";

import { captureAppError } from "@/utils/monitoring/sentry";
import { SCHEME_OVERVIEW_NS } from "./SchemeOverview.i18n";

import type {
    ApiErrorResponse,
    Curriculum,
    LinkedResource,
    UnitPlanResource,
    UnitPlan as UnitPlanType,
} from "@twinkltech/curriculum-architect-domain-hooks";


type UnitPlanProps = {
  unitInfo: UnitInfo | null;
  curriculum?: Curriculum | null;
  subject?: UnitPlanType["subject"];
  error?: ApiErrorResponse | null;
  isLoading: boolean;
  title?: string;
  description?: string;
  type: UnitPlanType["type"] | null;
  subjectId: string;
  keyArt?: string;
  resources: UnitPlanResource[];
  unitId: string;
  unitPack: {
    id: string;
    type: UnitPlanType["type"];
    title: string;
    canonicalUrl: string;
  } | null;
};

export const SchemeOverview = ({
  curriculum,
  description,
  error,
  isLoading = false,
  keyArt,
  resources,
  subject,
  title,
  unitId,
  unitInfo,
  unitPack,
}: UnitPlanProps) => {
  const { t } = useTranslation(SCHEME_OVERVIEW_NS);

  const pinnedResourcesList: LinkedResource[] =
    resources
      ?.filter(
        (resource): resource is LinkedResource =>
          "isPinned" in resource && !!resource.isPinned
      )
      // sort all items alphabetically first
      .sort((a, b) => {
        return a.title.localeCompare(b.title);
      })
      // and then by order prop. Anything without an order prop is last, with 0 being first, then 1, 2, 3 etc.
      .sort((a, b) => {
        if (a.order === undefined && b.order === undefined) {
          return 0;
        }
        if (a.order === undefined) {
          return 1;
        }
        if (b.order === undefined) {
          return -1;
        }

        return a.order - b.order;
      }) ?? [];

  const additionalResourcesList: LinkedResource[] =
    resources
      ?.filter(
        (resource): resource is LinkedResource =>
          "isPinned" in resource && !resource.isPinned
      )
      // sort all items alphabetically first
      .sort((a, b) => {
        return a.title.localeCompare(b.title);
      })
      // and then by order prop. Anything without an order prop is last, with 0 being first, then 1, 2, 3 etc.
      .sort((a, b) => {
        if (a.order === undefined && b.order === undefined) {
          return 0;
        }
        if (a.order === undefined) {
          return 1;
        }
        if (b.order === undefined) {
          return -1;
        }

        return a.order - b.order;
      }) ?? [];

  if (isLoading) {
    return <LoadingPage />;
  } else if (error || !subject || !curriculum) {
    captureAppError(error || new Error("Unknown error loading scheme overview"), {
      action: "load",
      area: "scheme",
    });
    return <GenericError />;
  }

  return (
    <ContentContainer>
      <UnitHeader
        curriculum={curriculum}
        description={description}
        keyArt={keyArt}
        subject={subject}
        title={title}
        unitId={unitId}
        unitInfo={unitInfo}
      />
      <div className="flex gap-800 justify-between">
        <div className="grow-1 max-w-[658px]">
          <nav aria-expanded="true" aria-label={t("unit-content")}>
            <ul>
              {unitPack && (
                <div
                  aria-labelledby="unit-content-id"
                  className="flex flex-col gap-3 mb-8"
                  role="group"
                >
                  <Text as="h2" id="unit-content-id" variant="xl-medium">
                    {t("unit-content")}
                  </Text>
                  <ResourceList>
                    <ResourceListItem>
                      <ResourceListItemLink
                        rel="noreferrer"
                        target="_blank"
                        to={`${paths.external.TWINKL_URL}${unitPack.canonicalUrl}`}
                      >
                        <span>
                          <span className="font-medium">
                            {ResourceHelper.getTypeLabel({
                              slug: unitPack.type,
                            })}
                          </span>
                          : {unitPack.title}
                        </span>
                      </ResourceListItemLink>
                    </ResourceListItem>
                  </ResourceList>
                </div>
              )}
              {pinnedResourcesList.length > 0 && (
                <div
                  aria-labelledby="planning-knowledge-id"
                  className="flex flex-col gap-3 mb-8"
                  role="group"
                >
                  <Text as="h2" id="planning-knowledge-id" variant="xl-medium">
                    {t("planning-and-knowledge")}
                  </Text>
                  <ResourceList>
                    {pinnedResourcesList.map((resource) => {
                      return (
                        <ResourceListItem key={resource.resourceId}>
                          <ResourceListItemLink
                            aria-labelledby={resource.resourceId}
                            rel="noopener noreferrer"
                            target="_blank"
                            to={`${paths.external.TWINKL_URL}${resource.canonicalUrl}`}
                            variant="planning-knowledge"
                          >
                            <span>
                              <span className="font-medium">
                                {ResourceHelper.getTypeLabel({
                                  slug: resource.type,
                                })}
                              </span>
                              : {resource.title}
                            </span>
                          </ResourceListItemLink>
                        </ResourceListItem>
                      );
                    })}
                  </ResourceList>
                </div>
              )}
              {additionalResourcesList.length > 0 && (
                <div
                  aria-labelledby="additional-resources-id"
                  className="flex flex-col gap-3"
                  role="group"
                >
                  <Text as="h2" variant="xl-medium">
                    {t("additional-resources")}
                  </Text>
                  <ResourceList>
                    {additionalResourcesList.map((resource) => (
                      <ResourceListItem key={resource.resourceId}>
                        <ResourceListItemLink
                          aria-label={resource.title}
                          rel="noopener noreferrer"
                          target="_blank"
                          to={`${paths.external.TWINKL_URL}${resource.canonicalUrl}`}
                          variant="additional-resource"
                        >
                          <span id="additional-resource-id">
                            <span className="font-medium">
                              {ResourceHelper.getTypeLabel({
                                slug: resource.type,
                              })}
                            </span>
                            {": "}
                            {resource.title}
                          </span>
                        </ResourceListItemLink>
                      </ResourceListItem>
                    ))}
                  </ResourceList>
                </div>
              )}
            </ul>
          </nav>
        </div>
      </div>
    </ContentContainer>
  );
};
